import React from 'react';
import s from './s.module.less';
import type { ProviderBillPayoutDetailVo } from 'types/providerBill';

type Props = Readonly<{
    data?: ProviderBillPayoutDetailVo;
}>;

export default function BillingPayoutReadOnly({ data }: Props) {
    const hasPartial = !!(
        data?.firstName ||
        data?.lastName ||
        data?.email ||
        data?.addressLine1 ||
        data?.companyName ||
        data?.taxId ||
        data?.accountNumber ||
        data?.routingNumber
    );
    const hasFull = !!(data?.accountNumber || data?.routingNumber);

    if (!hasPartial) {
        return (
            <div className={s.notSetUp}>
                <div className={s.title}>Not set up</div>
                <p className={s.desc}>
                    You haven&apos;t set up your payout account yet. Click &quot;Edit&quot; to get started.
                </p>
            </div>
        );
    }

    const addressDisplay = [
        data?.addressLine1,
        data?.addressLine2,
        data?.city && `${data.city}${data?.state ? `, ${data.state}` : ''} ${data?.zip || ''}`.trim(),
    ]
        .filter(Boolean)
        .join(', ');

    const taxIdDisplay =
        data?.taxIdType && data?.taxId
            ? `${data.taxIdType} - ${data.taxId}`
            : data?.taxIdType || data?.taxId || '-';

    return (
        <div className={s.wrap}>
            {hasFull && (
                <div className={s.successBanner}>
                    <svg className={s.icon} viewBox="0 0 20 20" fill="currentColor">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        />
                    </svg>
                    <div>
                        <p className={s.successTitle}>You&apos;re all set to receive payouts</p>
                        <p className={s.successDesc}>
                            Your Klarity earnings will be deposited directly into your connected bank account.
                        </p>
                    </div>
                </div>
            )}
            <div className={s.infoRow}>
                <div className={s.infoItem}>
                    <div className={s.infoLabel}>First name</div>
                    <div>{data?.firstName || '-'}</div>
                </div>
                <div className={s.infoItem}>
                    <div className={s.infoLabel}>Last name</div>
                    <div>{data?.lastName || '-'}</div>
                </div>
            </div>
            <div className={s.infoRow}>
                <div className={s.infoItem}>
                    <div className={s.infoLabel}>Address</div>
                    <div>{addressDisplay || '-'}</div>
                </div>
                <div className={s.infoItem}>
                    <div className={s.infoLabel}>Email</div>
                    <div>{data?.email || '-'}</div>
                </div>
            </div>
            <div className={s.infoRow}>
                <div className={s.infoItem}>
                    <div className={s.infoLabel}>Company name</div>
                    <div>{data?.companyName || '-'}</div>
                </div>
                <div className={s.infoItem}>
                    <div className={s.infoLabel}>Tax ID</div>
                    <div>{taxIdDisplay}</div>
                </div>
            </div>
            <div className={s.infoRow}>
                <div className={s.infoItem}>
                    <div className={s.infoLabel}>Bank account number</div>
                    <div>{data?.accountNumber ? `****${data.accountNumber.slice(-4)}` : '-'}</div>
                </div>
                <div className={s.infoItem}>
                    <div className={s.infoLabel}>Bank routing number</div>
                    <div>{data?.routingNumber ? `****${data.routingNumber.slice(-4)}` : '-'}</div>
                </div>
            </div>
        </div>
    );
}
